import { Key } from "client/Misc/commonTypes";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { outOfContextCall } from "client/Misc/reactUtils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { findLastIndex } from "client_legacy/ndaw/arrayHelpers";
import {
	createContext,
	KeyboardEvent,
	ReadonlyRefObject,
	useCallback,
	useContext,
	useMemo,
	useRef
} from "react";
import { useValueEqualMemo } from "./useValueEqualMemo";

export type KeyBoardListNavigationContextData = {
	focusPreviousItem: (index: number) => void;
	focusNextItem: (index: number) => void;
	setElement(index: number, element: HTMLElement | null): void;
	cancelNavigation(): void;
	navigationDirection: "vertical" | "horizontal";
};

export const KeyBoardListNavigationContext = createContext<KeyBoardListNavigationContextData>({
	focusPreviousItem: outOfContextCall("KeyBoardListNavigationContext", "getElement"),
	focusNextItem: outOfContextCall("KeyBoardListNavigationContext", "getElement"),
	setElement: outOfContextCall("KeyBoardListNavigationContext", "setElement"),
	cancelNavigation: outOfContextCall("KeyBoardListNavigationContext", "cancelNavigation"),
	navigationDirection: "vertical"
});

export type KeyboardListNavigationOptions = {
	onFocusInput?: () => void;
	onItemFocusChange?: (index: number) => void;
	navigationDirection?: "vertical" | "horizontal";
	onOutOfBounds?: (direction: "start" | "end") => void;
} & ({
	shouldCancelDirectlyFromItems: true;
	onCancel(): void;
} | {
	shouldCancelDirectlyFromItems?: false;
	onCancel?(): void;
}) ;

export type KeyboardListNavigationHandlers = {
	contextData: KeyBoardListNavigationContextData;
	focusFirstItemHandler(): void;
	focusItemAtIndexHandler(index: number): boolean;
	keyDownHandler(args: KeyboardEvent): void;
};

export function useKeyboardListNavigation(options: KeyboardListNavigationOptions = {}): KeyboardListNavigationHandlers {
	const {
		onFocusInput,
		onItemFocusChange,
		onCancel,
		shouldCancelDirectlyFromItems = false,
		navigationDirection = "vertical",
		onOutOfBounds
	} = options;

	const elementRefs: ReadonlyRefObject<(HTMLElement | null)[]> = useRef<(HTMLElement | null)[]>([]);

	const focusPreviousItemHandler = useCallback(
		(index: number): void => {
			const { current: refs } = elementRefs;

			if (index === 0) {
				onOutOfBounds?.("start");
				onFocusInput?.();
				return;
			}

			let target: number = index - 1;

			while (isNil(refs[target]) && target >= 1) {
				target -= 1;
			}

			const element: HTMLElement | null = refs[target];

			if (!element) {
				onOutOfBounds?.("start");
				onFocusInput?.();
			} else {
				element.focus();
				onItemFocusChange?.(target);
			}
 		},
		[
			onFocusInput,
			onItemFocusChange,
			onOutOfBounds
		]
	);

	const focusNextItemHandler = useCallback(
		(index: number): void => {
			const { current: refs } = elementRefs;
			const lastIndex: number = refs.length - 1;

			let target: number = index + 1;

			while (isNil(refs[target]) && target < lastIndex) {
				target += 1;
			}

			const element: HTMLElement | null = refs[target];

			if (isNil(element)) {
				onOutOfBounds?.("end");
				return;
			}

			element.focus();
			onItemFocusChange?.(target);
 		},
		[onItemFocusChange, onOutOfBounds]
	);

	const contextData = useMemo(
		(): KeyBoardListNavigationContextData => {
			return {
				focusPreviousItem: focusPreviousItemHandler,
				focusNextItem: focusNextItemHandler,
				setElement(index: number, element: HTMLElement | null) {
					elementRefs.current[index] = element;
					const indexOfLastSetRef: number = findLastIndex(
						elementRefs.current,
						isNotNil
					);

					elementRefs.current.length = indexOfLastSetRef + 1;
				},
				cancelNavigation(): void {
					onFocusInput?.();

					if (shouldCancelDirectlyFromItems) {
						ensureNotNil(onCancel)();
					}
				},
				navigationDirection
			};
		},
		[
			shouldCancelDirectlyFromItems,
			onCancel,
			onFocusInput,
			focusPreviousItemHandler,
			focusNextItemHandler,
			navigationDirection
		]
	);

	const focusItemAtIndexHandler = useCallback(
		(index: number): boolean => {
			const element: HTMLElement | null = elementRefs.current[index];

			if (!element) {
				return false;
			}

			element.focus();
			onItemFocusChange?.(index);
			return true;
		},
		[onItemFocusChange]
	);

	const focusFirstItemHandler = useCallback(
		(): void => {
			if (!focusItemAtIndexHandler(0)) {
				focusNextItemHandler(0);
			}
		},
		[focusItemAtIndexHandler, focusNextItemHandler]
	);

	const keyDownHandler = useCallback(
		(args: KeyboardEvent): void => {
			const key: Key | null = tryResolveKeyboardKey(args);

			switch (key) {
				case Key.ArrowDown:
						focusFirstItemHandler();
						args.preventDefault();
					break;
				case Key.Escape:
					onCancel?.();
					break;
				default:
					break;
			}
		},
		[
			focusFirstItemHandler,
			onCancel
		]
	);

	return useMemo(
		(): KeyboardListNavigationHandlers => {
			return {
				contextData,
				focusFirstItemHandler,
				focusItemAtIndexHandler,
				keyDownHandler
			};
		},
		[
			contextData,
			focusFirstItemHandler,
			focusItemAtIndexHandler,
			keyDownHandler
		]
	);
};

export type KeyboardListNavigationItemHandlers = {
	ref(element: HTMLElement | null): void;
	onKeyDown(args: KeyboardEvent<HTMLElement>): void;
};

export type KeyboardListNavigationItemOptions = {
	itemIndex: number;
	onExecute?: (key: Key) => void;
	executeKey?: Key | (readonly Key[]);
	shouldCancelOnTab?: boolean;
	shouldCancelAfterExecute?: boolean;
};

export function useKeyboardListNavigationItem(
	options: KeyboardListNavigationItemOptions
): KeyboardListNavigationItemHandlers {
	const {
		setElement,
		focusNextItem,
		focusPreviousItem,
		cancelNavigation,
		navigationDirection
	} = useContext(KeyBoardListNavigationContext);

	const {
		itemIndex,
		onExecute,
		executeKey = Key.Enter,
		shouldCancelOnTab = false,
		shouldCancelAfterExecute = false
	} = options;

	const executeKeys = useValueEqualMemo(
		(): ReadonlySet<Key> => {
			if (typeof executeKey === "string") {
				return new Set([executeKey]);
			}

			return new Set(executeKey);
		},
		[executeKey]
	);

	return useMemo(
		(): KeyboardListNavigationItemHandlers => ({
			ref(element: HTMLElement | null): void {
				setElement(itemIndex, element);
			},
			onKeyDown(args: KeyboardEvent): void {
				const key: Key | null = tryResolveKeyboardKey(args);

				if (!key) {
					return;
				}

				if (executeKeys.has(key) && isNotNil(onExecute)) {
					args.preventDefault();
					onExecute(key);

					if (shouldCancelAfterExecute) {
						cancelNavigation();
					}
				}

				switch (key) {
					case Key.ArrowUp:
						if (navigationDirection === "vertical") {
							focusPreviousItem(itemIndex);
							args.preventDefault();
						}
						break;
					case Key.ArrowDown:
						if (navigationDirection === "vertical") {
							focusNextItem(itemIndex);
							args.preventDefault();
						}
						break;
					case Key.ArrowLeft:
						if (navigationDirection === "horizontal") {
							focusPreviousItem(itemIndex);
							args.preventDefault();
						}
						break;
					case Key.ArrowRight:
						if (navigationDirection === "horizontal") {
							focusNextItem(itemIndex);
							args.preventDefault();
						}
						break;
					case Key.Escape:
						cancelNavigation();
						args.preventDefault();
						break;
					case Key.Tab:
						if (shouldCancelOnTab) {
							cancelNavigation();
							args.preventDefault();
						}
						break;
					default:
						break;
				}
			},
		}),
		[
			setElement,
			itemIndex,
			executeKeys,
			onExecute,
			shouldCancelAfterExecute,
			cancelNavigation,
			navigationDirection,
			shouldCancelOnTab,
			focusPreviousItem,
			focusNextItem
		]
	);
}